import { useState, useCallback, useEffect, useRef } from "react";
import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { db } from "../firebase/config";
// Import cache utilities
import {
  getCachedData,
  setCachedData,
  invalidateCache,
  incrementReadCount,
  getReadCount,
} from "./utils/cacheUtils"; // Adjust the import path as necessary

const DOC_PATH = "staticContent/default";

export const useStaticDefaultField = (fieldName) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  // Generate a unique key for this specific field in the document
  const cacheKey = fieldName ? `${DOC_PATH}/${fieldName}` : null;

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const safeSetState = (setter, value) => {
    if (isMounted.current) {
      setter(value);
    }
  };

  const fetchData = useCallback(async () => {
    if (!fieldName || !cacheKey) {
      // Ensure cacheKey is also valid
      console.warn(
        "[READ/CACHE] No fieldName provided to useStaticDefaultField."
      );
      safeSetState(setList, []);
      safeSetState(setError, "No field name specified.");
      return;
    }

    // --- Cache Check ---
    const cachedList = getCachedData(cacheKey);
    if (cachedList !== null) {
      safeSetState(setList, cachedList);
      safeSetState(setLoading, false); // Already have data
      safeSetState(setError, null);
      console.log(
        `[CACHE] Used cached data for field "${fieldName}". Total session reads: ${getReadCount()}`
      );
      return; // Don't fetch from Firestore
    }
    // --- End Cache Check ---

    // Only log actual Firestore reads and increment count here
    const currentReadCount = incrementReadCount(); // Increment and get new count
    console.log(
      `🔥 [READ #${currentReadCount}] Fetching field "${fieldName}" from ${DOC_PATH}`
    );
    safeSetState(setLoading, true);
    safeSetState(setError, null);

    try {
      const docRef = doc(db, DOC_PATH);
      const docSnap = await getDoc(docRef); // <<< ACTUAL FIRESTORE READ >>>

      if (docSnap.exists()) {
        const data = docSnap.data();
        const fetched = data?.[fieldName] || [];

        if (Array.isArray(fetched)) {
          safeSetState(setList, fetched);
          // --- Cache Update ---
          setCachedData(cacheKey, fetched);
          // --- End Cache Update ---
          console.log(
            `✅ [READ #${currentReadCount}] Fetched ${
              fetched.length
            } items for field "${fieldName}". Total session reads: ${getReadCount()}`
          );
        } else {
          console.warn(
            `⚠️ [READ #${currentReadCount}] Field "${fieldName}" in ${DOC_PATH} is not an array. Found:`,
            typeof fetched
          );
          safeSetState(setList, []);
          safeSetState(setError, `Field "${fieldName}" is not an array.`);
          // Optionally invalidate cache if structure is wrong?
          // invalidateCache(cacheKey);
        }
      } else {
        console.warn(
          `⚠️ [READ #${currentReadCount}] Document ${DOC_PATH} does not exist.`
        );
        safeSetState(setList, []);
        safeSetState(setError, `Document ${DOC_PATH} not found.`);
        // Invalidate cache if doc doesn't exist
        invalidateCache(cacheKey);
      }
    } catch (err) {
      console.error(
        `❌ [READ #${currentReadCount}] Error fetching field "${fieldName}":`,
        err
      );
      if (isMounted.current) {
        setError(`Failed to fetch ${fieldName}: ${err.message}`);
        setList([]); // Keep state consistent on error
      }
      // Invalidate cache on fetch error
      invalidateCache(cacheKey);
    } finally {
      safeSetState(setLoading, false);
    }
  }, [fieldName, cacheKey]); // Add cacheKey to dependency array

  const addItem = useCallback(
    async (newItem) => {
      if (!fieldName || !cacheKey) {
        // Check cacheKey
        setError("Field name not specified for adding.");
        return Promise.reject(new Error("Field name not specified."));
      }
      // ... (rest of validation)

      setLoading(true); // Use safeSetState if preferred
      setError(null);
      try {
        const docRef = doc(db, DOC_PATH);
        await updateDoc(docRef, {
          [fieldName]: arrayUnion(newItem),
        });

        // --- Cache Invalidation ---
        invalidateCache(cacheKey);
        // --- End Cache Invalidation ---

        // OPTIONAL: Optimistic update locally THEN refetch or rely on next read
        // safeSetState(setList, (prev) => [...prev, newItem]); // Optimistic add
        // console.log(`✅ [UPDATE] Added item to field "${fieldName}". Cache invalidated.`);
        // await fetchData(); // Force refetch to ensure consistency (will hit cache if called again quickly)
        // OR rely on next component mount/call to fetchData to get fresh data

        // --- FOR SIMPLICITY: Let's refetch immediately after invalidation ---
        console.log(
          `✅ [UPDATE] Added item to field "${fieldName}". Invalidating cache and refetching...`
        );
        await fetchData(); // This will now fetch fresh data from Firestore
        // --- End Refetch ---

        return Promise.resolve(newItem);
      } catch (err) {
        console.error(
          `❌ [UPDATE] Error adding item to field "${fieldName}":`,
          err
        );
        if (isMounted.current) {
          setError(`Failed to add item to ${fieldName}: ${err.message}`);
        }
        throw err;
      } finally {
        safeSetState(setLoading, false); // Loading state handled by fetchData now
      }
    },
    [fieldName, cacheKey, fetchData] // Added fetchData dependency
  );

  const deleteItem = useCallback(
    async (itemToDelete) => {
      if (!fieldName || !cacheKey) {
        // Check cacheKey
        setError("Field name not specified for deletion.");
        return Promise.reject(new Error("Field name not specified."));
      }
      // ... (rest of validation)

      setLoading(true);
      setError(null);
      try {
        const docRef = doc(db, DOC_PATH);
        await updateDoc(docRef, {
          [fieldName]: arrayRemove(itemToDelete),
        });

        // --- Cache Invalidation BEFORE Refetch ---
        invalidateCache(cacheKey);
        // --- End Cache Invalidation ---

        console.log(
          `✅ [UPDATE] Deleted item from field "${fieldName}". Invalidating cache and triggering refetch.`
        );
        await fetchData(); // Refetch the list from Firestore (will be a fresh read)

        return Promise.resolve();
      } catch (err) {
        console.error(
          `❌ [UPDATE] Error deleting item from field "${fieldName}":`,
          err
        );
        if (isMounted.current) {
          setError(`Failed to delete item from ${fieldName}: ${err.message}`);
        }
        throw err;
      } finally {
        // setLoading handled by fetchData
        // safeSetState(setLoading, false); // Let fetchData handle the final loading state
      }
    },
    [fieldName, cacheKey, fetchData] // Added cacheKey and fetchData
  );

  // Initial fetch logic remains the same
  useEffect(() => {
    if (isMounted.current) {
      fetchData();
    }
  }, [fetchData]);

  return {
    list,
    loading,
    error,
    addItem,
    deleteItem,
    fetchData, // Keep exposed if needed
  };
};
